import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";

interface CertificationFormProps {
  initialData?: {
    id?: string;
    name: string;
    issuer?: string | null;
    issued_date?: string | null;
  };
  onSave: (data: {
    name: string;
    issuer?: string | null;
    issued_date?: string | null;
  }) => void;
  onCancel: () => void;
}

const CertificationForm = ({ initialData, onSave, onCancel }: CertificationFormProps) => {
  const [name, setName] = useState(initialData?.name || "");
  const [issuer, setIssuer] = useState(initialData?.issuer || "");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [errors, setErrors] = useState<{
    name?: string;
    issuer?: string;
    date?: string;
  }>({});

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const years = Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - i);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: typeof errors = {};

    if (!name.trim()) {
      newErrors.name = "Title is required";
    }

    if (!month || !year) {
      newErrors.date = "Issue date is required";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      const issued_date = `${year}-${String(months.indexOf(month) + 1).padStart(2, '0')}-01`;
      onSave({ name, issuer: issuer || null, issued_date });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="title">
          Title <span className="text-red-500">*</span>
        </Label>
        <Input
          id="title"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setErrors(prev => ({ ...prev, name: undefined }));
          }}
          className={errors.name ? "border-red-500" : ""}
        />
        {errors.name && (
          <p className="text-sm text-red-500">{errors.name}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="issuer">Issuer</Label>
        <Input
          id="issuer"
          value={issuer}
          onChange={(e) => {
            setIssuer(e.target.value);
            setErrors(prev => ({ ...prev, issuer: undefined }));
          }}
          className={errors.issuer ? "border-red-500" : ""}
        />
        {errors.issuer && (
          <p className="text-sm text-red-500">{errors.issuer}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label>
          Issue date <span className="text-red-500">*</span>
        </Label>
        <div className="grid grid-cols-2 gap-4">
          <Select
            value={month}
            onValueChange={(value) => {
              setMonth(value);
              setErrors(prev => ({ ...prev, date: undefined }));
            }}
          >
            <SelectTrigger className={errors.date ? "border-red-500" : ""}>
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={year.toString()}
            onValueChange={(value) => {
              setYear(value);
              setErrors(prev => ({ ...prev, date: undefined }));
            }}
          >
            <SelectTrigger className={errors.date ? "border-red-500" : ""}>
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={y.toString()}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {errors.date && (
          <p className="text-sm text-red-500">{errors.date}</p>
        )}
      </div>

      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          Save
        </Button>
      </div>
    </form>
  );
};

export default CertificationForm;